// askSmart-ui/src/pages/TOSP/tospMigration.ts
import { loadCollection } from "./tospStore";
import { saveConfiguration, saveRecord, TospApiError } from "./tospApi";
import {
  Building,
  BudgetYear,
  DEFAULT_CONFIGURATION,
  normalizeConfiguration,
  normalizeUnit,
  Occupancy,
  TaxYear,
  Tenant,
  tenantLabel,
  TospConfiguration,
  Unit,
} from "./types";

// Set once this browser's copy has been imported or the prompt explicitly
// dismissed. The local records themselves are deliberately left in place — the
// import overwrites the server with them, so keeping them costs nothing and is
// the only copy if an import turns out to have gone wrong. Nothing reads them
// any more: budgets were the last holdout and moved to the backend too.
const HANDLED_KEY = "tosp:localDataHandled";

export function localDataHandled(): boolean {
  try {
    return !!localStorage.getItem(HANDLED_KEY);
  } catch {
    return false;
  }
}

export function markLocalDataHandled(): void {
  try {
    localStorage.setItem(HANDLED_KEY, new Date().toISOString());
  } catch {
    /* private mode / quota — the prompt reappearing is the only cost */
  }
}

export interface MigrationSummary {
  buildings: number;
  units: number;
  taxYears: number;
  tenants: number;
  occupancies: number;
  /** Budgets held locally from before they were stored on the backend. */
  budgets: number;
  configuration: boolean;
}

/** What's sitting in this browser, without writing anything. */
export function inspectLocalData(): MigrationSummary {
  const buildings = loadCollection<Building>("buildings", []);
  const units = loadCollection<Unit>("units", []).map(normalizeUnit);
  const tenants = loadCollection<Tenant>("tenants", []);

  return {
    buildings: buildings.length,
    units: units.length,
    taxYears: units.reduce((sum, unit) => sum + (unit.taxYears?.length || 0), 0),
    tenants: tenants.length,
    occupancies: tenants.reduce((sum, tenant) => sum + (tenant.occupancies?.length || 0), 0),
    budgets: loadCollection<BudgetYear>("budgets", []).length,
    configuration: !!localStorage.getItem("tosp:configuration"),
  };
}

// Names the record that failed, so a rejected import says which one and why
// rather than just "something went wrong". A 403 here almost always means the
// caller holds the module's `.view` permission but not `.admin` — reads work,
// writes don't — which the backend console confirms on the
// `[checkAnyPermission]` line.
async function step<T>(what: string, work: () => Promise<T>): Promise<T> {
  try {
    return await work();
  } catch (err) {
    const detail =
      err instanceof TospApiError
        ? `${err.message} (HTTP ${err.status})`
        : (err as Error)?.message || "unknown error";
    throw new Error(`Failed on ${what} — ${detail}`);
  }
}

// Pushes everything this browser holds up to the backend, one record at a
// time, splitting the nested tax years and occupancies into their own
// documents as the API expects.
//
// Records keep their existing ids, so running it twice overwrites rather than
// duplicates — but it will overwrite whatever is on the server with the local
// copy, which is why it's an explicit action rather than something automatic.
// It stops at the first failure; nothing local is cleared either way, so a
// half-finished import can simply be re-run once the cause is fixed.
export async function migrateLocalDataToBackend(): Promise<MigrationSummary> {
  const buildings = loadCollection<Building>("buildings", []);
  const units = loadCollection<Unit>("units", []).map(normalizeUnit);
  const tenants = loadCollection<Tenant>("tenants", []);
  const stored = localStorage.getItem("tosp:configuration");

  for (const building of buildings) {
    await step(`building "${building.name}"`, () =>
      saveRecord<Building>("buildings", building),
    );
  }

  let taxYears = 0;
  for (const unit of units) {
    const { taxYears: years, ...fields } = unit;
    await step(`unit "${unit.suiteNumber}"`, () =>
      saveRecord<Omit<Unit, "taxYears">>("units", fields as Omit<Unit, "taxYears">),
    );
    for (const year of years || []) {
      await step(`unit "${unit.suiteNumber}" tax year ${year.year}`, () =>
        saveRecord<TaxYear & { unitId: string }>("tax-years", {
          ...year,
          unitId: unit.id,
        }),
      );
      taxYears += 1;
    }
  }

  let occupancies = 0;
  for (const tenant of tenants) {
    const { occupancies: leases, ...fields } = tenant;
    await step(`tenant "${tenantLabel(tenant)}"`, () =>
      saveRecord<Omit<Tenant, "occupancies">>(
        "tenants",
        fields as Omit<Tenant, "occupancies">,
      ),
    );
    for (const occupancy of leases || []) {
      await step(`an occupancy for "${tenantLabel(tenant)}"`, () =>
        saveRecord<Occupancy & { tenantId: string }>("occupancies", {
          ...occupancy,
          tenantId: tenant.id,
        }),
      );
      occupancies += 1;
    }
  }

  // Budgets were the last collection to move to the backend, so a browser that
  // was used before that holds the only copy of whatever was worked up in it —
  // imported QuickBooks lines, category splits, the progression and any manual
  // adjustments. Each is one document, written whole.
  const budgets = loadCollection<BudgetYear>("budgets", []);
  for (const budget of budgets) {
    await step(`budget "${budget.name?.trim() || budget.year}"`, () =>
      saveRecord<BudgetYear>("budgets", budget),
    );
  }

  if (stored) {
    const configuration = normalizeConfiguration({
      ...DEFAULT_CONFIGURATION,
      ...(JSON.parse(stored) as TospConfiguration),
    });
    await step("the configuration", () =>
      saveConfiguration<TospConfiguration>(configuration),
    );
  }

  // Only once everything above landed — a failed import leaves the prompt up.
  markLocalDataHandled();

  return {
    buildings: buildings.length,
    units: units.length,
    taxYears,
    tenants: tenants.length,
    occupancies,
    budgets: budgets.length,
    configuration: !!stored,
  };
}
